import { X } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { IconButton } from "../IconButton/IconButton";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;

  actions?: ReactNode;

  "aria-label"?: string;

  /** Accessible name of the close button. */
  closeLabel?: string;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  actions,
  "aria-label": ariaLabel,
  closeLabel = "Close",
}: Readonly<ModalProps>) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    const focusable = dialog?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    (focusable?.[0] ?? dialog)?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab" || !dialog) return;
      const nodes = dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <>
      <div aria-hidden="true" className="fixed inset-0 z-[100] bg-surface-overlay" onClick={onClose} />
      <div className="pointer-events-none fixed inset-0 z-[101] flex items-start justify-center p-6 pt-[10vh]">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={title ? undefined : ariaLabel}
          aria-labelledby={title ? titleId : undefined}
          tabIndex={-1}
          className="pointer-events-auto flex max-h-[80vh] w-full max-w-md flex-col rounded-2xl border border-border-input bg-surface-elevated shadow-xl animate-modal-in"
        >
          {title && (
            <div className="flex items-start gap-3 px-6 pt-6">
              <span id={titleId} className="block min-w-0 flex-1 text-content-primary [font:var(--type-h3)]">
                {title}
              </span>
              <IconButton variant="ghost" size="md" aria-label={closeLabel} icon={<X />} onClick={onClose} />
            </div>
          )}
          <div
            className={["min-h-0 flex-1 overflow-y-auto px-6", title ? "mt-4" : "mt-6", actions ? "" : "pb-6"].join(
              " ",
            )}
          >
            {children}
          </div>
          {actions && <div className="flex flex-wrap items-center justify-end gap-2 px-6 mt-6 pb-6">{actions}</div>}
        </div>
      </div>
    </>,
    document.body,
  );
}
